/**
 * OnboardingGates - Gate check functions for tutorial steps
 * Each function returns a Promise<boolean> indicating if the step is complete
 */

import { supabase } from '@/integrations/supabase/client';
import type { BillingStatus } from '@/hooks/useBillingStatus';

export interface GateCheckContext {
  shopId: string;
  userId: string;
}

/**
 * Step 1: Click Profile Button - detects navigation to shop profile
 */
export const checkClickProfile = async (_ctx: GateCheckContext): Promise<boolean> => {
  if (typeof window === 'undefined') return false;
  return window.location.pathname === '/shop-profile';
};

/**
 * Step 2: General Information Complete
 * Checks: shop name, address, and time zone are all filled
 */
export const checkGeneralInfoComplete = async (ctx: GateCheckContext): Promise<boolean> => {
  try {
    const { data: profile } = await supabase
      .from('shop_profile')
      .select('name, time_zone')
      .eq('shop_id', ctx.shopId)
      .maybeSingle();

    if (!profile) return false;
    if (!profile.name || profile.name.trim() === '') return false;
    if (!profile.time_zone) return false;

    return true;
  } catch (error) {
    console.error('Gate check error (general-info):', error);
    return false;
  }
};

/**
 * Step 3: Vehicle Packages Selected
 * Checks: at least one vehicle package is selected
 */
export const checkVehiclePackagesComplete = async (ctx: GateCheckContext): Promise<boolean> => {
  try {
    const { data: profile } = await supabase
      .from('shop_profile')
      .select('vehicle_packages')
      .eq('shop_id', ctx.shopId)
      .maybeSingle();

    if (!profile) return false;
    const packages = profile.vehicle_packages as string[] | null;
    return packages && packages.length > 0;
  } catch (error) {
    console.error('Gate check error (vehicle-packages):', error);
    return false;
  }
};
/**
 * Step 4a: Click Hours & Holidays Tab
 * Checks the URL tab parameter is 'hours' on the shop profile page
 */
export const checkClickHoursTab = async (_ctx: GateCheckContext): Promise<boolean> => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return window.location.pathname === '/shop-profile' && params.get('tab') === 'hours';
};

/**
 * Step 4: Shop Hours Complete
 * Checks: at least one weekday has hours defined
 */
export const checkShopHoursComplete = async (ctx: GateCheckContext): Promise<boolean> => {
  try {
    const { data: profile } = await supabase
      .from('shop_profile')
      .select('hours')
      .eq('shop_id', ctx.shopId)
      .maybeSingle();

    if (!profile) return false;

    const hours = profile.hours as Record<string, { open: string; close: string }>;
    if (!hours || typeof hours !== 'object') return false;
    
    const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const hasHours = weekdays.some(day => {
      const dayHours = hours[day];
      return dayHours && dayHours.open && dayHours.close;
    });

    return hasHours;
  } catch (error) {
    console.error('Gate check error (shop-hours):', error);
    return false;
  }
};

/**
 * Step 5: Holidays (soft gate - optional)
 */
export const checkHolidaysComplete = async (_ctx: GateCheckContext): Promise<boolean> => {
  return true; // Soft gate
};

/**
 * Step 6a: Click Shop Culture Tab
 * Checks the URL tab parameter is 'culture' on the shop profile page
 */
export const checkClickCultureTab = async (_ctx: GateCheckContext): Promise<boolean> => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return window.location.pathname === '/shop-profile' && params.get('tab') === 'culture';
};

/**
 * Step 7: Shop Culture (soft gate - optional)
 */
export const checkShopCultureComplete = async (_ctx: GateCheckContext): Promise<boolean> => {
  return true; // Soft gate
};

/**
 * Step 8: Save Profile (soft gate - always passes)
 */
export const checkSaveProfile = async (_ctx: GateCheckContext): Promise<boolean> => {
  return true; // Soft gate
};

/**
 * Save Layout (soft gate - always passes)
 */
export const checkSaveLayout = async (_ctx: GateCheckContext): Promise<boolean> => {
  return true; // Soft gate
};

/**
 * Check Email (soft gate - always passes)
 */
export const checkCheckEmail = async (_ctx: GateCheckContext): Promise<boolean> => {
  return true; // Soft gate
};

export const checkViewAcceptedTech = async (_ctx: GateCheckContext): Promise<boolean> => {
  return true; // Soft gate
};

export const checkNavigateToProfile = async (_ctx: GateCheckContext): Promise<boolean> => {
  if (typeof window === 'undefined') return false;
  return window.location.pathname === '/shop-profile';
};

export const checkClickTechniciansTab = async (_ctx: GateCheckContext): Promise<boolean> => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return window.location.pathname === '/shop-profile' && params.get('tab') === 'technicians';
};

export const checkClickEditTech = async (_ctx: GateCheckContext): Promise<boolean> => {
  return true; // Soft gate
};

export const checkTechGeneralInfo = async (_ctx: GateCheckContext): Promise<boolean> => {
  return true; // Soft gate
};

export const checkClickSkillsTab = async (_ctx: GateCheckContext): Promise<boolean> => {
  return true; // Soft gate
};

export const checkTechProficiencies = async (ctx: GateCheckContext): Promise<boolean> => {
  // Hard gate - check if technician has at least one proficiency set
  const { data: technicians } = await supabase
    .from('technicians')
    .select('proficiencies')
    .eq('shop_id', ctx.shopId)
    .limit(1);
  
  if (!technicians || technicians.length === 0) return false;
  
  const proficiencies = technicians[0].proficiencies as Record<string, number> | null;
  return proficiencies !== null && Object.keys(proficiencies).length > 0;
};

export const checkClickScheduleTab = async (_ctx: GateCheckContext): Promise<boolean> => {
  return true; // Soft gate
};

export const checkTechSchedule = async (_ctx: GateCheckContext): Promise<boolean> => {
  return true; // Soft gate
};

export const checkSaveTechProfile = async (_ctx: GateCheckContext): Promise<boolean> => {
  return true; // Soft gate
};

/**
 * Step 10: Click Birdseye Button - detects navigation to birdseye
 */
export const checkClickBirdseye = async (_ctx: GateCheckContext): Promise<boolean> => {
  if (typeof window === 'undefined') return false;
  return window.location.pathname === '/birdseye';
};

/**
 * Step 11: Click Configure Layout Button - detects navigation to shop floor editor
 */
export const checkClickConfigureLayout = async (_ctx: GateCheckContext): Promise<boolean> => {
  if (typeof window === 'undefined') return false;
  return window.location.pathname === '/shop-floor/editor';
};

/**
 * Step 13: Click Team Button - detects navigation to team management
 */
export const checkClickTeam = async (_ctx: GateCheckContext): Promise<boolean> => {
  if (typeof window === 'undefined') return false;
  return window.location.pathname === '/team-management';
};

/**
 * Step 9: Billing Complete
 * Checks: subscription is active, trialing, alpha, internal, or has can_access_app
 */
export const checkBillingComplete = async (ctx: GateCheckContext): Promise<boolean> => {
  try {
    const { data, error } = await supabase.rpc('get_shop_billing_status', {
      p_shop_id: ctx.shopId,
    });

    if (error || !data || data.length === 0) return false;

    const status = data[0] as BillingStatus;
    
    // Allow internal and alpha shops
    if (status.billing_status === 'internal' || status.billing_status === 'alpha') {
      return true;
    }

    // Allow if can_access_app is true (covers active, trialing, and grace period)
    return status.can_access_app === true;
  } catch (error) {
    console.error('Gate check error (billing):', error);
    return false;
  }
};

/**
 * Step 3: Birdseye Tour (soft gate - always passes after visited)
 */
export const checkBirdseyeViewed = async (_ctx: GateCheckContext): Promise<boolean> => {
  // Soft gate - automatically marked complete when user proceeds
  return true;
};

/**
 * Step 4: Bay Exists
 * Checks: at least one active bay exists for the shop
 */
export const checkBayExists = async (ctx: GateCheckContext): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('shop_bays')
      .select('id')
      .eq('shop_id', ctx.shopId)
      .eq('is_active', true)
      .limit(1);

    if (error) return false;
    return data && data.length > 0;
  } catch (error) {
    console.error('Gate check error (bay-exists):', error);
    return false;
  }
};

/**
 * Step 5: Invite Sent
 * Checks: at least one team invitation exists for the shop
 */
export const checkInviteSent = async (ctx: GateCheckContext): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('team_invitations')
      .select('id')
      .eq('shop_id', ctx.shopId)
      .limit(1);

    if (error) return false;
    return data && data.length > 0;
  } catch (error) {
    console.error('Gate check error (invite-sent):', error);
    return false;
  }
};

/**
 * Step 6: Tech Assigned to Bay
 * Checks: at least one bay has an assigned technician
 */
export const checkTechAssignedToBay = async (ctx: GateCheckContext): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('shop_bays')
      .select('assigned_tech_id')
      .eq('shop_id', ctx.shopId)
      .not('assigned_tech_id', 'is', null)
      .limit(1);

    if (error) return false;
    return data && data.length > 0;
  } catch (error) {
    console.error('Gate check error (tech-assigned):', error);
    return false;
  }
};

/**
 * Step 7: First Ticket (soft gate - always passes)
 */
export const checkFirstTicketCreated = async (_ctx: GateCheckContext): Promise<boolean> => {
  // Soft gate - automatically marked complete when user proceeds
  return true;
};

/**
 * Tech Step 1: Welcome (soft gate)
 */
export const checkTechWelcomeViewed = async (_ctx: GateCheckContext): Promise<boolean> => {
  return true;
};

/**
 * Tech Step 2: Technician Profile Saved
 * Checks: technician_users binding exists for this user
 */
export const checkTechProfileSaved = async (ctx: GateCheckContext): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('technician_users')
      .select('technician_id')
      .eq('user_id', ctx.userId)
      .eq('shop_id', ctx.shopId)
      .limit(1);

    if (error) return false;
    return data && data.length > 0;
  } catch (error) {
    console.error('Gate check error (tech-profile):', error);
    return false;
  }
};

/**
 * Tech Step 3: Board Tour (soft gate)
 */
export const checkTechBoardTourViewed = async (_ctx: GateCheckContext): Promise<boolean> => {
  return true;
};

/**
 * User Step: Start Job Timer (soft gate)
 * Passes when the Play button is present on the ticket card
 */
export const checkStartJobTimerVisible = async (_ctx: GateCheckContext): Promise<boolean> => {
  if (typeof document === 'undefined') return true; // don't block in non-DOM contexts
  return !!document.querySelector('[data-testid="timer-start"]');
};

/**
 * User Step: Technician Passport Intro (soft gate)
 */
export const checkTechnicianPassportIntro = async (_ctx: GateCheckContext): Promise<boolean> => {
  return true;
};

/**
 * User Step: Technician Passport Details (soft gate)
 */
export const checkTechnicianPassportDetails = async (_ctx: GateCheckContext): Promise<boolean> => {
  return true;
};

/**
 * User guide onboarding checks
 */
export const checkAccountMenuVisible = async (_ctx: GateCheckContext): Promise<boolean> => {
  if (typeof window === 'undefined') return false;
  return !!document.querySelector('[data-testid="account-menu-button"]');
};

export const checkUserGuideLinkVisible = async (_ctx: GateCheckContext): Promise<boolean> => {
  if (typeof window === 'undefined') return false;
  return !!document.querySelector('[data-testid="user-guide-link"]');
};

// Gate check registry - maps step IDs to their check functions
export const GATE_CHECKS: Record<string, (ctx: GateCheckContext) => Promise<boolean>> = {
  'click-profile': checkClickProfile,
  'general-info': checkGeneralInfoComplete,
  'vehicle-packages': checkVehiclePackagesComplete,
  'click-hours-tab': checkClickHoursTab,
  'shop-hours': checkShopHoursComplete,
  'holidays': checkHolidaysComplete,
  'click-culture-tab': checkClickCultureTab,
  'shop-culture': checkShopCultureComplete,
  'save-profile': checkSaveProfile,
  'click-birdseye': checkClickBirdseye,
  'click-configure-layout': checkClickConfigureLayout,
  'create-bay': checkBayExists,
  'save-layout': checkSaveLayout,
  'click-team': checkClickTeam,
  'invite-member': checkInviteSent,
  'check-email': checkCheckEmail,
  'view-accepted-tech': checkViewAcceptedTech,
  'navigate-to-profile': checkNavigateToProfile,
  'click-technicians-tab': checkClickTechniciansTab,
  'click-edit-tech': checkClickEditTech,
  'tech-general-info': checkTechGeneralInfo,
  'click-skills-tab': checkClickSkillsTab,
  'tech-proficiencies': checkTechProficiencies,
  'click-schedule-tab': checkClickScheduleTab,
  'tech-schedule': checkTechSchedule,
  'save-tech-profile': checkSaveTechProfile,
  'birdseye-tour': checkBirdseyeViewed,
  'assign-tech': checkTechAssignedToBay,
  'first-ticket': checkFirstTicketCreated,
  'tech-welcome': checkTechWelcomeViewed,
  'tech-profile': checkTechProfileSaved,
  'tech-board-tour': checkTechBoardTourViewed,
  'start-job-timer': checkStartJobTimerVisible,
  'technician-passport-intro': checkTechnicianPassportIntro,
  'technician-passport-details': checkTechnicianPassportDetails,
  'user-guide-intro': checkAccountMenuVisible,
  'user-guide-select': checkUserGuideLinkVisible,
  'user-guide-intro-user': checkUserGuideLinkVisible,
  'account-settings-intro': checkAccountMenuVisible,
  'account-settings-review': async () => true,
  'user-guide-review': async () => true,
};

/**
 * Check if a specific step's gate is satisfied
 */
export const checkStepGate = async (
  stepId: string,
  ctx: GateCheckContext
): Promise<boolean> => {
  const checkFn = GATE_CHECKS[stepId];
  if (!checkFn) {
    console.warn(`No gate check function for step: ${stepId}`);
    return true; // Default to passing if no check defined
  }
  return checkFn(ctx);
};
